import { type FirstStepKey, getTown } from '@wyn/content';
import { balance, FIRST_STEPS, FIRST_STEPS_DONE_REWARD, firstStepRewardText } from '@wyn/game-core';
import type { PlayerSnapshot } from '@wyn/contracts';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { UI } from '../icons/ui-icons.ts';
import { useAction } from '../lib/queries.ts';
import { Icon } from './Icon.tsx';
import { ErrorNotice } from './Notice.tsx';

const anchor = (id: string, label: string) => <a href={`#${id}`} className="text-gilt hover:underline">{label}</a>;

/** Where each step is done, from where the player stands: a section of this page, or another page. */
function where(key: FirstStepKey, p: PlayerSnapshot): ReactNode {
  switch (key) {
    case 'train': return anchor('training-yard', 'To the Training Yard');
    case 'venue': return p.town === p.homeTown ? anchor('venues', 'To the venues') : null;
    case 'shop': return anchor('shops', 'To the shops');
    case 'road': return anchor('roads', 'To the roads');
    case 'trade': return <Link to="/market" className="text-gilt hover:underline">To the Market</Link>;
    case 'fight': return anchor('folk', 'To the folk about town');
    case 'bank':
      return p.town === balance.bank.town
        ? <Link to="/bank" className="text-gilt hover:underline">To the Royal Bank</Link>
        : anchor('roads', `Take the road to ${getTown(balance.bank.town).name}`);
  }
}

/**
 * The first-steps chain at the top of Town, until every step is done or the player puts it aside. Steps
 * can be done in any order; the first one not yet done is picked out.
 */
export function FirstSteps({ player: p }: { player: PlayerSnapshot }) {
  const dismiss = useAction('dismissFirstSteps');
  const fs = p.firstSteps;
  if (!fs || fs.dismissed !== null) return null;
  const left = FIRST_STEPS.filter((s) => fs.done[s.key] === undefined);
  if (left.length === 0) return null;
  const next = left[0]!.key;
  return (
    <section className="panel space-y-3 border-gilt/60" aria-labelledby="first-steps">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="first-steps" className="heading"><Icon name={UI.road} className="mr-1 text-gilt" />First steps</h2>
        <span className="flex items-baseline gap-3">
          <span className="font-mono text-sm text-ink-faint" data-testid="first-steps-count">
            {FIRST_STEPS.length - left.length} of {FIRST_STEPS.length}
          </span>
          <button type="button" className="btn-quiet py-0 text-xs" disabled={dismiss.isPending} onClick={() => dismiss.mutate({})}>
            Put aside
          </button>
        </span>
      </div>
      <p className="text-sm text-ink-dim">
        Find your feet in the realm. Each step pays a little, and the last pays {firstStepRewardText(FIRST_STEPS_DONE_REWARD)} more.
      </p>
      <ol className="space-y-2">
        {FIRST_STEPS.map((s) => {
          const done = fs.done[s.key] !== undefined;
          return (
            <li key={s.key} data-testid="first-step" data-done={done} aria-current={s.key === next ? 'step' : undefined} className={`flex gap-3 ${s.key === next ? '' : 'opacity-70'}`}>
              <span aria-hidden className={`w-4 shrink-0 text-center ${done ? 'text-verdigris' : s.key === next ? 'text-gilt' : 'text-ink-faint'}`}>{done ? '✓' : '○'}</span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className={done ? 'text-ink-dim line-through decoration-rule' : ''}>
                    {s.name}
                    {done && <span className="sr-only"> (done)</span>}
                  </span>
                  <span className="text-xs text-ink-faint">{firstStepRewardText(s.reward)}</span>
                </div>
                {!done && (
                  <div className="text-sm text-ink-dim">
                    {s.hint(p.homeTown)} <span className="whitespace-nowrap">{where(s.key, p)}</span>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      <ErrorNotice error={dismiss.error} />
    </section>
  );
}
